/* eslint-disable max-lines-per-function */
import ParameterValidator from '@mvps-genesis/parameter-validator';
import EventBus from '@mvps-genesis/event-bus';
import {MessageContainer, Message} from 'extensions/messages';

/*
 *--------------------------------------------------------------------------
 * AjaxAddToCart
 *--------------------------------------------------------------------------
 *
 * Asynchronously handle the form submission of a Miva ADPR form.
 *
 */
export default class AjaxAddToCart {

	/**
	 * Parameter Validator class object
	 *
	 * @var	{ParameterValidator} @mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * The form element
	 *
	 * @var	{HTMLElement}
	 */
	formElement;

	/**
	 * The submit button element
	 *
	 * @var	{HTMLElement}
	 */
	submitButtonElement;

	/**
	 * The action input element [name="action"]
	 *
	 * @var	{HTMLElement}
	 */
	actionInputElement;

	/**
	 * Text to apply to the submit button element when the form submission is processing
	 *
	 * @var	{String}
	 */
	processingButtonText;

	/**
	 * The sucess message object
	 *
	 * @var	{Object}
	 */
	successMessage;

	/**
	 * Selector for stock message errors
	 *
	 * @var	{String}
	 */
	stockMessageErrorSelector;

	/**
	 * The missing attributes message object
	 *
	 * @var	{Object}
	 */
	missingAttributesMessage;

	/**
	 * The limited stock message object
	 *
	 * @var	{Object}
	 */
	limitedStockMessage;

	/**
	 * The out of stock message object
	 *
	 * @var	{Object}
	 */
	outOfStockMessage;

	/**
	 * The "other" message object
	 *
	 * @var	{Object}
	 */
	otherMessage;

	/**
	 * MessageContainer class object
	 *
	 * @var {MessageContainer} extensions/messages
	 */
	messageContainer;

	/**
	 * Property to update the text value for the submit button element
	 *
	 * @var {String}
	 */
	submitButtonTextProperty;

	/**
	 * The submit button element text value
	 *
	 * @var {String}
	 */
	submitButtonText;

	/**
	 * Scroll to message object
	 *
	 * @var {Object}
	 */
	scrollToMessage;

	/**
	 * Create a new AjaxAddToCart object.
	 *
	 * @param  {HTMLElement|String}  formElement                        The form element.
	 * @param  {HTMLElement|String}  options.actionInputElement         The action input element. Defaults to 'input[name="Action"]'.
	 * @param  {Object}              options.limitedStockMessage        The limited stock message object.
	 * @param  {Object}              options.missingAttributesMessage   The missing attributes message object.
	 * @param  {Object}              options.otherMessage               The "other" message object.
	 * @param  {Object}              options.outOfStockMessage          The out of stock message object.
	 * @param  {String}              options.processingButtonText       Text to apply to the submit button element when the form submission is processing. Defaults to 'Processing...'.
	 * @param  {HTMLElement|String}  options.responseMessageElement     The message element to target for response messages. Defaults to '.js-add-to-cart-message'.
	 * @param  {Boolean|String}      options.scrollToMessage            Scroll to message mode setting, which controls scroll to message container behavior after add to cart action. Supports the following values: true = always scroll to message container; false = never scroll to message container; 'success' = only scroll to message container on successful add to cart; 'error' = only scroll to message container on unsuccessful add to cart. Defaults to false.
	 * @param  {Object}              options.scrollToMessageOptions     Scroll to message options object, supports the Element.scrollIntoView() options listed here - https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView. Defaults to {behavior: 'smooth', block: 'center'}.
	 * @param  {String}              options.stockMessageErrorSelector  Selector for stock message errors. Defaults to '.g-messages--error'.
	 * @param  {HTMLElement|String}  options.submitButtonElement        The submit button element. Defaults to '.js-add-to-cart'.
	 * @param  {Object}              options.successMessage             The success message object.
	 */
	constructor (
		formElement,
		{
			actionInputElement = 'input[name="Action"]',
			limitedStockMessage = {
				dynamic: true,
				message: 'We do not have enough of the combination you have selected.<br />Please adjust your quantity.',
				options: {
					autohide: true,
					type: 'warning'
				}
			},
			missingAttributesMessage = {
				message: 'All <em class="u-color-error">Required</em> options have not been selected.<br />Please review the following options: <span class="u-color-error">%missingAttributes%</span>',
				options: {
					autohide: true,
					type: 'warning'
				}
			},
			otherMessage = {
				message: 'Please review your selection.',
				options: {
					autohide: true,
					type: 'warning'
				}
			},
			outOfStockMessage = {
				dynamic: true,
				message: 'The combination you have selected is out of stock.<br />Please review your options or check back later.',
				options: {
					autohide: true,
					type: 'warning'
				}
			},
			processingButtonText = 'Processing...',
			responseMessageElement = '.js-add-to-cart-message',
			scrollToMessage = false,
			scrollToMessageOptions = {
				behavior: 'smooth',
				block: 'center'
			},
			stockMessageErrorSelector = '.g-messages--error',
			submitButtonElement = '.js-add-to-cart',
			successMessage = {
				message: 'Added to cart.',
				options: {
					autohide: true,
					type: 'success'
				}
			}
		} = {}
	) {
		// Create validator
		this.#validator = new ParameterValidator(this);

		// Set properties from options
		this.formElement = this.#validator.isElement('formElement', formElement);
		this.submitButtonElement = this.#validator.isElement('options.submitButtonElement', submitButtonElement, {container: this.formElement});
		this.actionInputElement = this.#validator.isElement('options.actionInputElement', actionInputElement, {container: this.formElement});
		this.processingButtonText = this.#validator.isString('options.processingButtonText', processingButtonText);
		this.successMessage = this.#validator.isObject('options.successMessage', successMessage);
		this.stockMessageErrorSelector = this.#validator.isString('options.stockMessageErrorSelector', stockMessageErrorSelector);
		this.missingAttributesMessage = this.#validator.isObject('options.missingAttributesMessage', missingAttributesMessage);
		this.limitedStockMessage = this.#validator.isObject('options.limitedStockMessage', limitedStockMessage);
		this.outOfStockMessage = this.#validator.isObject('options.outOfStockMessage', outOfStockMessage);
		this.otherMessage = this.#validator.isObject('options.otherMessage', otherMessage);

		// Create message container from option
		this.messageContainer = new MessageContainer({container: this.#validator.isElement('options.responseMessageElement', responseMessageElement, {container: this.formElement})});

		// Set cached / computed values
		this.submitButtonTextProperty = this.submitButtonElement instanceof HTMLButtonElement ? 'textContent' : 'value';
		this.submitButtonText = this.submitButtonElement[this.submitButtonTextProperty];

		// Set scroll to message properties
		this.scrollToMessage = {
			mode: this.#validator.inSet('options.scrollToMessage', scrollToMessage, [
				true,
				false,
				'success',
				'error'
			]),
			scrollIntoView: this.#validator.isObject('options.scrollToMessageOptions', scrollToMessageOptions)
		};

		// Run bootstrap functions
		this.bindEvents();
	}

	/**
	 * Bind form element events.
	 */
	bindEvents () {
		this.formElement.addEventListener('submit', this.onSubmit.bind(this), false);
	}

	/**
	 * Handle on submit events.
	 *
	 * @param  {Event} event    The submit event.
	 * @return {Undefined|void} Returns undefined if the action input element's value is not equal to ADPR.
	 */
	async onSubmit (event) {
		if (this.actionInputElement.value !== 'ADPR') {
			return;
		}

		event.preventDefault();
		event.stopImmediatePropagation();

		// Set form action
		if (this.submitButtonElement.dataset.action) {
			this.formElement.action = this.submitButtonElement.dataset.action;
		}

		const formData = new FormData(this.formElement);
		const request = new Request(this.formElement.action, {
			body: formData,
			method: this.formElement.method || 'POST'
		});

		let payload = {
			data: null,
			form: this.formElement,
			formData: formData,
			message: null,
			request: request,
			success: false
		};

		// Disable form submission while request is processing and set text of submit element to processing text
		this.submitButtonElement.disabled = true;
		this.submitButtonElement[this.submitButtonTextProperty] = this.processingButtonText;

		// Clear response messages
		this.messageContainer.clear();

		// Send the HTTP request
		const response = await fetch(request);

		if (response.ok) {
			const responseHtml = await response.text();
			const responseDom = new DOMParser().parseFromString(responseHtml, 'text/html');
			const pageCodeResponseHeader = response.headers.get('g-mm-page-code');

			payload.data = responseDom;

			switch (pageCodeResponseHeader) {
				case 'BASK':
					this.onSuccess();

					payload.success = true;

					break;

				case 'PATR':
					this.onMissingAttributes();

					break;

				case 'PROD':
					this.onMissingAttributes();

					break;

				case 'PLMT':
					this.onLimitedStock(payload);

					break;

				case 'POUT':
					this.onOutOfStock(payload);

					break;

				default:
					this.onOther();

					break;
			}

			payload.message = this.messageContainer.currentMessage.message;

			this.handleScrollToMessage(pageCodeResponseHeader === 'BASK');
		} else {
			const errorMessage = `${this.constructor.name} - The request failed with a status of: ${response.status}`;

			// What do when the request fails
			payload.message = errorMessage;
		}

		this.submitButtonElement.disabled = false;
		this.submitButtonElement[this.submitButtonTextProperty] = this.submitButtonText;

		// Dispatch global addToCart event
		EventBus.dispatchEvent('MVPS:addToCart', payload);
	}

	/**
	 * Handle scroll to message functionality.
	 *
	 * @param  {Boolean} isSuccess Flag to specify if the add to cart response was successful.
	 * @return {Undefined|void}    Returns undefined if the scroll to message functionality should not run.
	 */
	handleScrollToMessage (isSuccess) {
		if (this.scrollToMessage.mode === false) {
			return;
		}

		this.#validator.isBoolean('handleScrollToMessage.isSuccess', isSuccess);

		let scrollToMessage = this.scrollToMessage.mode === true;

		// eslint-disable-next-line no-extra-parens
		if (!scrollToMessage && ((isSuccess && this.scrollToMessage.mode === 'success') || (!isSuccess && this.scrollToMessage.mode === 'error'))) {
			scrollToMessage = true;
		}

		if (!scrollToMessage) {
			return;
		}

		this.messageContainer.container.scrollIntoView(this.scrollToMessage.scrollIntoView);
	}

	/**
	 * Handle successful add to cart response.
	 */
	onSuccess () {
		this.messageContainer.add(new Message(this.successMessage.message, this.successMessage.options));

		// Re-initialize Attribute Machine (if it is active)
		if (typeof attrMachCall !== 'undefined') {
			// eslint-disable-next-line new-cap
			window.attrMachCall.Initialize();
		}
	}

	/**
	 * Handle missing attributes add to cart response.
	 */
	onMissingAttributes () {
		const findRequired = this.formElement.querySelectorAll('.g-product-layout-purchase__options .is-required');
		const missingAttributes = [];

		for (let i = 0, len = findRequired.length; i < len; i++) {
			missingAttributes.push(findRequired[i].title ? findRequired[i].title : findRequired[i].textContent);
		}

		this.messageContainer.add(new Message(this.missingAttributesMessage.message.replace('%missingAttributes%', missingAttributes.join(', ')), this.missingAttributesMessage.options));
	}

	/**
	 * Handle limited stock add to cart response.
	 *
	 * @param  {Object} payload The response payload object
	 */
	onLimitedStock (payload) {
		let message = this.limitedStockMessage.message;

		if (typeof payload.data === 'object' && this.limitedStockMessage.dynamic === true) {
			const payloadErrorMessage = this.getStockMessage(payload.data);

			if (payloadErrorMessage !== '') {
				message = payloadErrorMessage;
			}
		}

		this.messageContainer.add(new Message(message, this.limitedStockMessage.options));
	}

	/**
	 * Handle out of stock add to cart response.
	 *
	 * @param  {Object} payload The response payload object
	 */
	onOutOfStock (payload) {
		let message = this.outOfStockMessage.message;

		if (typeof payload.data === 'object' && this.outOfStockMessage.dynamic === true) {
			const payloadErrorMessage = this.getStockMessage(payload.data);

			if (payloadErrorMessage !== '') {
				message = payloadErrorMessage;
			}
		}

		this.messageContainer.add(new Message(message, this.outOfStockMessage.options));
	}

	/**
	 * Handle "other" add to cart response. This is a catch all method for response handling.
	 */
	onOther () {
		this.messageContainer.add(new Message(this.otherMessage.message, this.otherMessage.options));
	}

	/**
	 * Get the stock message element from the response payload data.
	 *
	 * @param  {Document} payloadData The Document object returned by the response payload data.
	 * @return {DOMString|String}     The stock message element's inner HTML or an empty string if the stock message element does not exist.
	 */
	getStockMessage (payloadData) {
		const stockMessageElement = payloadData.querySelector(this.stockMessageErrorSelector);

		if (stockMessageElement) {
			return stockMessageElement.innerHTML;
		}

		return '';
	}

};
